import React from "react";
import { statusColors } from "../../../orders/OrderSidePanal";
import { ThemeBasedColorHeader } from "src/shared/modules/profile/theme/themeCheacker";

const PAYMENT_METHODS = ["Cash", "Card"];

type OrderItem = {
    Orderdate: string;
    Ordertime: string;
    orderid?: string;
    Total_price?: number;
    ReceivedAmount?: number;
    discount?: number;
    RefundAmount?: number;
    refundAmount?: number;
    RemainingAmount?: number;
    CurrentTotalRemaining?: number;
    order_status?: string; // ✅ added
};

type Props = {
    orders?: OrderItem[];
    selectedOrderId: string;
    onRowSelect: (orderAmount: number, orderId: string) => void;
    onNavigate: (orderId: string) => void;
};

const isPaymentMethod = (id?: string) =>
    PAYMENT_METHODS.includes(id || "");
const GRID =
    "grid grid-cols-[4%_12%_18%_14%_10%_10%_10%_10%_12%]";

function OrderTable({
    orders,
    selectedOrderId,
    onRowSelect,
    onNavigate,
}: Props) {
    const reversed = orders ? [...orders].reverse() : [];

    const orderIdClick = (e: React.MouseEvent, orderId: string) => {
        e.stopPropagation();

        // Extract base order ID (before -R- or any suffix)
        const baseOrderId = orderId.split("-")[0];

        // Optional: validate format (D + digits)
        const isValid = /^D\d+$/.test(baseOrderId);

        if (isValid) {
            onNavigate(baseOrderId);
        } else {
            console.warn("Invalid Order ID:", orderId);
        }
    };

    return (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden flex flex-col flex-1 min-h-0">
            {/* Header */}
            <div className="px-4 py-3 border-b border-slate-100">
                <span className="text-sm font-bold text-slate-800">
                    Order history
                </span>
            </div>

            {/* Table Head */}
            <div
                className={`${GRID} px-4 py-2 ${ThemeBasedColorHeader()} border-b border-slate-200 text-[11px] uppercase tracking-wider sticky top-0 z-10`}
            >
                <div>#</div>
                <div>Date</div>
                <div>Order ID</div>
                <div>Status</div>
                <div className="text-right">Order(QR)</div>
                <div className="text-right">Payment(QR)</div>
                <div className="text-right">Refund(QR)</div>
                <div className="text-right">Balance(QR)</div>
                <div className="text-right">OSB(QR)</div>
            </div>

            {/* Body */}
            <div className="overflow-y-auto flex-1">
                {reversed.map((item, index) => {
                    const orderAmount = item.Total_price ?? 0;
                    const payment = item.ReceivedAmount ?? 0;

                    // ✅ FIX: Always positive refund
                    const refund = Math.abs((item.refundAmount || item.RefundAmount) ?? 0);

                    const balance = (Number(orderAmount) - Number(payment) - Number(refund));

                    const osb = item.CurrentTotalRemaining ?? 0;

                    const isPurePayment =
                        isPaymentMethod(item.orderid) &&
                        orderAmount === 0;

                    const orderStatus = item.order_status || "Unknown";
                    const isSelected =
                        item.orderid === selectedOrderId;

                    return (
                        <div
                            key={index}
                            onClick={() => {
                                if (!isPurePayment && orderAmount > 0) {
                                    onRowSelect(
                                        orderAmount,
                                        item.orderid || ""
                                    );
                                }
                            }}
                            className={[
                                GRID,
                                "px-4 py-2.5 border-b text-[13px] items-center transition",
                                isSelected
                                    ? "bg-blue-50"
                                    : index % 2 === 0
                                        ? "bg-white hover:bg-slate-50"
                                        : "bg-slate-50/50 hover:bg-slate-100",
                                !isPurePayment && orderAmount > 0
                                    ? "cursor-pointer"
                                    : "cursor-default",
                            ].join(" ")}
                        >
                            {/* S.No */}
                            <div className="text-slate-400 text-xs">
                                {index + 1}
                            </div>

                            {/* Date */}
                            <div>
                                <div className="text-[12px] text-slate-700">
                                    {item.Orderdate}
                                </div>
                                <div className="text-[11px] text-slate-400">
                                    {item.Ordertime}
                                </div>
                            </div>

                            {/* Order ID */}
                            <div className="min-w-0 overflow-hidden pr-2">
                                {isPaymentMethod(item.orderid) ? (
                                    <span className="truncate block text-slate-500">
                                        {item.orderid}
                                    </span>
                                ) : (
                                    <span
                                        className="truncate block text-blue-600 underline cursor-pointer"
                                        onClick={(e) => orderIdClick(e, item.orderid || "")}
                                    >
                                        {item.orderid}
                                    </span>
                                )}
                            </div>

                            {/* Status */}
                            <div>
                                <span
                                    className={`px-2 py-1 rounded text-xs font-semibold ${statusColors[orderStatus] ||
                                        "bg-gray-200 text-gray-700"
                                        }`}
                                >
                                    {orderStatus}
                                </span>
                            </div>

                            {/* Order */}
                            <div className="text-right text-blue-700 font-semibold">
                                {orderAmount > 0
                                    ? orderAmount.toFixed(2)
                                    : "—"}
                            </div>

                            {/* Payment */}
                            <div className="text-right text-green-600 font-semibold">
                                {payment > 0
                                    ? payment.toFixed(2)
                                    : "—"}
                            </div>

                            {/* Refund */}
                            <div className="text-right text-amber-600 font-semibold">
                                {refund > 0
                                    ? refund.toFixed(2)
                                    : "—"}
                            </div>

                            {/* Balance */}
                            <div
                                className={`text-right font-semibold ${isPurePayment
                                    ? "text-slate-400"
                                    : balance > 0
                                        ? "text-red-600"
                                        : "text-green-600"
                                    }`}
                            >
                                {isPurePayment
                                    ? "—"
                                    : Math.abs(balance).toFixed(2)}
                            </div>

                            {/* OSB */}
                            <div className="text-right text-violet-700 font-bold">
                                {osb.toFixed(2)}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export default OrderTable;